import { jsx as _jsx } from "react/jsx-runtime";
import { camelToDisplay } from '@iotv/datamodel';
import { SortOrder } from '@iotv/iotv-v3-types';
import { RemoveCircle } from '@mui/icons-material';
import { Skeleton, Stack } from '@mui/material';
import { useContext } from 'react';
import { ObjectViewType } from '../../types/AppTypes';
import { useColumnVisibilityForDataGrid } from '../../v2/components/generic/ObjectsDataTable/hooks/useColumnVisibility';
import { StripedDataGrid } from '../factories/DataGrid';
import { ObservationsContext } from './Contexts/OvesrvationsContextType_V2';
import { UseObservationsContext } from './Contexts/UseObservationsContext';
import { ObservationInputMetaKeys } from './fns';
import { DateColRendererEditWithApi, DateTimeColRendererWithApi, DefaultCellEditRendererWithApi, DefaultCellRendererWithApi, durationValueGetter, } from './gridColFns';
import { metricItemsToVOBs } from './helperFns';
const debug = process.env.REACT_APP_DEBUG && false;
export const ObservationDataGrid = (props) => {
    const { state: ObservationContextState, dispatch } = useContext(ObservationsContext);
    const { persistedObservationHistory, save, deleteObject, working, querying, errMessage } = useContext(UseObservationsContext);
    const { observationHistory } = ObservationContextState;
    const observationHistoryStore = observationHistory?.observedHistory ?? null;
    debug && console.log('historyStore', observationHistoryStore);
    const observationKeys = (observationHistoryStore?.keys() ?? []).filter((k) => !ObservationInputMetaKeys.includes(k));
    const observatinStateSnapshots = observationHistoryStore ? observationHistoryStore.getStateSnapshotArray(SortOrder.DESCENDING) : [];
    const pseudoTypeId = `HistoryStore_observation`;
    const observationMetricItems = observationHistory
        ? metricItemsToVOBs({
            psuedoParentVob: observationHistory,
            pseudoTypeId,
            stateSnapshots: observatinStateSnapshots,
        })
        : [];
    const handleDeleteObservation = (gp) => {
        debug && console.log(`handleDeleteObservation got `, gp);
        const key = gp.row.timestamp;
        const idxInMetricItems = observationMetricItems.findIndex((item) => item.timestamp === key);
        const idx = observationMetricItems.length - 1 - idxInMetricItems; // reverse index to match original order in store
        debug && console.log('Want to delete ', { idx, observationMetricItems });
        const deleteAction = { type: 'deleteObservation', value: { idx } };
        dispatch(deleteAction);
    };
    const [columnVisibilityModel] = useColumnVisibilityForDataGrid({
        dataTableReference: 'Observations',
        objectTypeId: 'HistoryStore',
        viewType: ObjectViewType.List,
    });
    const baseCols = [
        {
            headerName: '',
            field: 'id',
            renderCell: (gp) => _jsx(RemoveCircle, { onClick: () => handleDeleteObservation(gp), sx: { cursor: 'pointer' } }),
            width: 16,
        },
        // { field: 'localTimeStr', headerName: 'Time', width: 250 },
        {
            field: 'fromTimestampMs',
            headerName: 'From',
            flex: 1,
            renderCell: DateTimeColRendererWithApi,
            editable: true,
            renderEditCell: DateColRendererEditWithApi,
        },
        {
            field: 'toTimestampMs',
            headerName: 'To',
            flex: 1,
            renderCell: DateTimeColRendererWithApi,
            editable: true,
            renderEditCell: DateColRendererEditWithApi,
        },
        { field: '_duraction', headerName: 'Duration', width: 150, valueGetter: durationValueGetter },
    ];
    // Default passthrough renderer for DataGrid cells
    const dynamicCols = observationKeys.map((k) => {
        const colDef = {
            field: k,
            headerName: camelToDisplay(k),
            editable: true,
            flex: 1,
            renderCell: DefaultCellRendererWithApi,
            renderEditCell: DefaultCellEditRendererWithApi,
        };
        return colDef;
    });
    const descriptionsCol = {
        field: 'description',
        headerName: 'Description',
        editable: true,
        flex: 1,
        renderCell: DefaultCellRendererWithApi,
        renderEditCell: DefaultCellEditRendererWithApi,
    };
    const columns = [...baseCols, ...dynamicCols, descriptionsCol];
    const processRowUpdate = (newRow, oldRow) => {
        debug && console.log(`processRowUpdate got `, { newRow, oldRow });
        const idx = observationMetricItems.findIndex((item) => item.id === newRow.id);
        debug && console.log('processRowUpdate edit ', { idx, observationMetricItems });
        if (observationHistoryStore) {
            const idx = observationHistoryStore.timestamp.findIndex((t) => newRow.timestamp === t);
            const action = { type: 'editObservation', value: { idx, newRow, oldRow } };
            console.log(`TEMP processRowUpdate dispatching edit action`, { action });
            dispatch(action);
        }
        else {
            console.log(`TEMP processRowUpdate no observationHistoryStore, cannot dispatch edit action`);
        }
        return newRow;
    };
    const tableRowHeight = 24;
    const dataGridProps = {
        columns,
        columnHeaderHeight: tableRowHeight,
        rowHeight: tableRowHeight,
        rows: [...observationMetricItems].reverse(), // reverse to show most recent at top
        loading: querying,
        columnVisibilityModel,
        autoHeight: true,
        processRowUpdate,
        onProcessRowUpdateError: (e) => {
            console.log(e.message);
        },
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
    };
    return (_jsx(Stack, { gap: 1, width: '100%', children: querying ? (_jsx(Skeleton, { variant: 'rounded', height: '8em' }, "ObsGridSkeleton")) : (_jsx(StripedDataGrid, { ...dataGridProps }, 'ObservationGrid')) }, 'ObservationGridStack'));
};
